const INVOICE_QUANTITY_DECIMAL_DIGITS = 8

type QuantityRow = {
  id?: number
  qty?: number
}

const normalizeQuantity = (value: number | null | undefined): number => {
  const numericValue = Number(value ?? 0)
  return Number.isFinite(numericValue)
    ? Number(numericValue.toFixed(INVOICE_QUANTITY_DECIMAL_DIGITS))
    : Number.NaN
}

export const calculateRemainingInvoiceQuantity = (
  originalQuantity: number | undefined,
  returnedQuantity: number
) =>
  Number(
    (normalizeQuantity(originalQuantity) - normalizeQuantity(returnedQuantity)).toFixed(
      INVOICE_QUANTITY_DECIMAL_DIGITS
    )
  )

export const getPositiveReturnQuantityRows = <TRow extends QuantityRow>(rows: TRow[]): TRow[] =>
  (rows || [])
    .map((row) => ({...row, qty: normalizeQuantity(row.qty)}))
    .filter((row) => Number.isFinite(row.qty) && row.qty > 0) as TRow[]

export const applyReturnQuantitiesToInvoiceRows = <TRow extends QuantityRow>(
  sourceRows: TRow[],
  returnRows: QuantityRow[]
): TRow[] => {
  const returnQuantityById = new Map(
    (returnRows || []).map((row) => [row.id, normalizeQuantity(row.qty)])
  )

  return (sourceRows || []).map((row) => ({
    ...row,
    qty: calculateRemainingInvoiceQuantity(row.qty, returnQuantityById.get(row.id) ?? 0),
  })) as TRow[]
}

export const getSalesReturnQuantityError = (
  sourceRows: QuantityRow[],
  returnRows: QuantityRow[]
): string | undefined => {
  const sourceById = new Map(sourceRows.map((row, index) => [row.id, {row, index}]))
  let positiveRowCount = 0

  for (const returnRow of returnRows || []) {
    const returnedQuantity = normalizeQuantity(returnRow.qty)
    const source = sourceById.get(returnRow.id)
    const rowNumber = (source?.index ?? 0) + 1

    if (!Number.isFinite(returnedQuantity)) return `مقدار برگشت قلم شماره ${rowNumber} معتبر نیست.`
    if (returnedQuantity < 0) return `مقدار برگشت قلم شماره ${rowNumber} نمی‌تواند منفی باشد.`
    if (returnedQuantity === 0) continue

    positiveRowCount += 1
    if (!source) return 'یک یا چند قلم برگشتی در صورتحساب مرجع پیدا نشد.'

    const effectiveRemaining = normalizeQuantity(source.row.qty)
    if (returnedQuantity > effectiveRemaining)
      return `مقدار برگشت قلم شماره ${rowNumber} نمی‌تواند از مانده مؤثر ${effectiveRemaining} بیشتر باشد.`
  }

  if (positiveRowCount === 0)
    return 'حداقل یک ردیف با مقدار برگشت بزرگ‌تر از صفر وارد کنید.'

  const returnedQuantityById = new Map(
    (returnRows || []).map((row) => [row.id, normalizeQuantity(row.qty)])
  )
  const hasRemainingQuantity = sourceRows.some(
    (row) =>
      calculateRemainingInvoiceQuantity(row.qty, returnedQuantityById.get(row.id) ?? 0) > 0
  )

  return hasRemainingQuantity
    ? undefined
    : 'مانده مؤثر کل صورتحساب صفر می‌شود؛ برای برگشت کامل صورتحساب ابطالی صادر کنید.'
}
